<template>
  <div class="thank-you mx-auto mb-12 max-w-6xl lg:mt-20">
    <div class="bg-EXPONDO-grey-5 px-5 py-16 lg:rounded-b-lg lg:pt-4 lg:pb-24 min-h-160">
      <OrderHeading
        class="mb-5 gap-3"
        :class="isGuest ? 'lg:px-10' : 'lg:px-60'"
        :payment-status="paymentStatus"
        :is-order-payment-type-bank="isOrderPaymentTypeBank"
        :is-payment-type-cod="isOrderPaymentTypeCod"
      />
      <template v-if="order">
        <div :class="isGuest ? 'lg:mx-10' : 'lg:mx-32'">
          <SectionTransition
            :has-content="isOrderPaymentTypeBank && isBankInfoReady"
            client-only
          >
            <BankInfo />
          </SectionTransition>
          <SectionTransition
            :has-content="isOrderPaymentTypeAdyen && isMultibancoInfoReady"
            client-only
          >
            <MultibancoInfo />
          </SectionTransition>
        </div>
        <div :class="isGuest ? 'lg:grid lg:grid-cols-2 lg:gap-x-12 lg:items-start lg:px-10' : 'lg:px-60'">
          <div>
            <h2 class="mb-4 text-center text-2xl lg:text-left">
              {{ $t('Summary') }}
            </h2>
            <OrderDetails :order="order" />
            <CartItemsList
              :read-only="true"
            />
            <SkeletonLoader
              v-if="loading && (!lineItems || !lineItems.length)"
              height="107px"
            />

            <div class="mt-8">
              <h3 class="mb-4 text-center text-xl lg:text-left">
                {{ $t('Price') }}
              </h3>
              <PriceSummary :order="order" />
            </div>
          </div>
          <ThankYouRegistration
            :order="order"
            class="mt-8 mb-6 lg:my-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-8"
          />
          <div>
            <CustomLink
              :text="$t('View Your Order Status')"
              :url="myOrderUrl"
              :is-block="true"
              class="text-center"
            />
            <SectionTransition
              v-if="showTrustPilotWidget"
              has-content
              reserve-height="52px"
              client-only
            >
              <Trustbox
                :template-id="REVIEW_COLLECTOR_TEMPLATE_ID"
                style-height="52px"
                :locale="languageAndCountry"
                class="mt-5"
              />
            </SectionTransition>
          </div>
        </div>
      </template>
      <SkeletonLoader
        v-else-if="loading"
        class="lg:px-60"
        item-classes="mt-8"
        :number-of-items="7"
        height="12px"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Logger } from '~~/helpers/logger';
import type { LineItem } from '~~/types/commercetools';
import CartItemsList from '~/components/organisms/Cart/CartItemsList.vue';
import PriceSummary from '~/components/molecules/Checkout/ThankYou/PriceSummary.vue';
import OrderDetails from '~/components/molecules/Checkout/ThankYou/OrderDetails.vue';
import OrderHeading from '~/components/molecules/Checkout/ThankYou/OrderHeading/OrderHeading.vue';
import CustomLink from '~/components/atoms/Link/CustomLink.vue';
import ThankYouRegistration from '~/components/organisms/Checkout/ThankYouRegistration/ThankYouRegistration.vue';
import { ROUTES } from '~~/constants/routes';
import { CHECKOUT_STEPS } from '~~/constants/bloomreach';
import { MERCHANT_ID } from '~~/constants/checkout';

import { getFirstChildOrSelf } from '~~/helpers/dataStructure/getFirstChildOrSelf';
import { CUSTOM_QUERIES } from '~~/constants/customQueries';
import orderToPaymentType from '~~/helpers/Checkout/orderToPaymentType';
import { REVIEW_COLLECTOR_TEMPLATE_ID } from '~~/constants/trustUrls';
import { getLastPayment } from '~~/helpers/cart/getLastPayment';
import { getProductGTIN } from '~~/helpers/Checkout/getProductGTIN';
import { isGuestOrder } from '~~/helpers/Checkout/isGuestOrder';
import estimatedDeliveryDate from '~~/helpers/Checkout/estimatedDeliveryDate';
import { useShipping } from '~/utils/ct';
import type { ShippingMethodDeliveryInformation } from '~~/types/checkout/ShippingMethod';

definePageMeta({
  middleware: 'thank-you-requires-order',
  layout: 'default-full-width',
});

const bankInfoLoader = () => import(/* webpackChunkName: "BankInfo" */
  '~/components/molecules/Checkout/ThankYou/BankInfo/BankInfo.vue');
const multibancoInfoLoader = () => import(/* webpackChunkName: "MultibancoInfo" */
  '~/components/molecules/Checkout/ThankYou/MultibancoInfo/MultibancoInfo.vue');

export default defineComponent({
  name: 'ThankYou',
  components: {
    CartItemsList,
    PriceSummary,
    OrderDetails,
    OrderHeading,
    CustomLink,
    ThankYouRegistration,
    BankInfo: bankInfoLoader,
    MultibancoInfo: multibancoInfoLoader,
  },
  setup() {

    const route = useRoute();
    const { event: ciaEvent } = useCia();
    const { trackPurchase } = useGtmEvents();
    const { getAdjustedSlug } = useRouteExtended();
    const { deliveryDate } = useGTMDeliveryDate();
    const { $renderGCRSurvey } = useNuxtApp() as unknown as {
      $renderGCRSurvey: (payload: Record<string, unknown>) => Promise<unknown>;
    };
    const { hasGoogleReviewsEnabled, load: loadStore } = useStoreVsfExtended();
    const myOrderUrl = computed(() => getAdjustedSlug(ROUTES.MY_ORDERS));

    const orderId = computed<string | null>(() => {

      const id = getFirstChildOrSelf(route.query.order);

      return typeof id === 'string' && id.length ? id : null;

    });
    const {
      loading: loadingUserOrder,
      order,
      search,
      isOrderPaymentTypeBank,
      isOrderPaymentTypeCod,
      isOrderPaymentTypeAdyen,
    } = useUserOrderExtended();

    // BankInfo/MultibancoInfo are code-split; track when their chunk has actually resolved so
    // SectionTransition doesn't swap them in before they're ready to render.
    const isBankInfoReady = ref(false);
    const isMultibancoInfoReady = ref(false);

    watch(isOrderPaymentTypeBank, (isBank) => {

      if (isBank) {

        bankInfoLoader().then(() => {

          isBankInfoReady.value = true;

        });

      }

    }, { immediate: true });

    watch(isOrderPaymentTypeAdyen, (isAdyen) => {

      if (isAdyen) {

        multibancoInfoLoader().then(() => {

          isMultibancoInfoReady.value = true;

        });

      }

    }, { immediate: true });

    const { callConversionEvent } = useTypesenseGtmEvents();

    const { lineItems, lineItemsWithGuaranteeCombined } = useLineItems();
    const { products: productsTagManager, loadProducts } = useCheckoutTagManager();

    const { shipping: address } = useShipping();
    const { paymentStatus } = usePaymentStatus();

    const {
      loadingModule: {
        setLoading,
        loading: loadingCheckout,
      },
    } = useCheckout();
    const { ciaState } = useCiaState();

    const { countryCode, languageAndCountry } = useLocalization();

    const { showTrustPilotWidget } = useTrustPilotWidget();

    const paymentType = computed(() => orderToPaymentType(order.value));

    const isGuest = computed(() => isGuestOrder(order.value));

    const paymentName = computed(() => getLastPayment(order.value)?.paymentMethodInfo?.name);

    const loading = computed(() => loadingUserOrder.value || loadingCheckout.value);

    const searchOrder = async () => {

      if (!orderId.value) {

        return;

      }

      await search({
        customQuery: CUSTOM_QUERIES.THANK_YOU_PAGE_ORDER,
        id: orderId.value,
      });

    };

    const ensureData = async () => {

      setLoading(true);

      try {

        const tasks: Promise<unknown>[] = [];

        if (!order.value || order.value.id !== orderId.value) {

          tasks.push(searchOrder());

        }

        tasks.push(loadProducts());

        await Promise.all(tasks);

      } finally {

        setLoading(false);

      }

    };

    useAsyncData('thank-you', async () => {

      await ensureData();

      if (!order.value) {

        return navigateTo(getAdjustedSlug(ROUTES.HOME));

      }

      return '';

    });

    onMounted(async () => {

      // Needed because order can be empty when redirecting from a bank (buildRedirectUrlAfterAuth)
      await ensureData();

      if (order.value) {

        try {

          ciaEvent.checkout({
            step: CHECKOUT_STEPS.thankYou,
            cartId: ciaState.value.cartId,
            email: ciaState.value.email,
            userId: ciaState.value.userId,
          });

        } catch (error) {

          Logger.error(`cia|checkout error: ${error}`);

        }

        await loadStore();

        const surveyEventPromise = hasGoogleReviewsEnabled.value ? callSurveyEvent() : Promise.resolve();
        const triggerPurchasePromise = trackPurchase({
          cartOrOrder: order.value,
          products: productsTagManager.value,
          orderNumber: order.value.orderNumber,
          deliveryDate: deliveryDate.value,
          paymentType: paymentType?.value ?? undefined,
          paymentName: paymentName.value,
          purchaseUserInfo: {
            email: order.value.customerEmail ?? '',
            phone: address?.value?.phone ?? '',
          },
        });
        const conversionEventPromise = callConversionEvent(lineItems.value);

        await Promise.all([surveyEventPromise, triggerPurchasePromise, conversionEventPromise]);

      } else {

        return navigateTo(getAdjustedSlug(ROUTES.HOME));

      }

    });

    const callSurveyEvent = async () => {

      const gtin = productsTagManager.value.map(product => ({ gtin: getProductGTIN(product) }));

      const orderShippingMethod = (order.value as unknown as {
        shippingInfo?: { shippingMethod?: ShippingMethodDeliveryInformation };
      })?.shippingInfo?.shippingMethod;

      const resolvedEstimatedDeliveryDate = deliveryDate.value
        ?? estimatedDeliveryDate(languageAndCountry.value, orderShippingMethod);

      const payload = {
        merchant_id: MERCHANT_ID[countryCode.value as keyof typeof MERCHANT_ID],
        estimated_delivery_date: resolvedEstimatedDeliveryDate,
        order_id: order.value.id,
        delivery_country: countryCode.value?.toUpperCase(),
        email: order.value?.customerEmail,
        products: gtin,
      };

      if (typeof ($renderGCRSurvey as unknown) !== 'function') {

        Logger.error('gcr|$renderGCRSurvey is not registered (plugin not loaded?)');

        return;

      }

      try {

        await ($renderGCRSurvey as unknown as (p: Record<string, unknown>) => Promise<unknown>)(payload);

      } catch (error) {

        // Keep checkout resilient but expose details when debugging.
        Logger.error('[GCR Survey][thank-you] Error during survey rendering', error);

      }

    };

    const getItemSlug = (item: LineItem) => `/${item.productSlug}`;

    return {
      getItemSlug,
      loading,
      order,
      isGuest,
      myOrderUrl,
      lineItems,
      lineItemsWithGuaranteeCombined,
      isOrderPaymentTypeBank,
      isOrderPaymentTypeCod,
      isOrderPaymentTypeAdyen,
      isBankInfoReady,
      isMultibancoInfoReady,
      showTrustPilotWidget,
      languageAndCountry,
      countryCode,
      REVIEW_COLLECTOR_TEMPLATE_ID,
      paymentStatus,
    };

  },
});
</script>

<style scoped lang="scss">
.thank-you {
  &:deep(.theme--light.v-skeleton-loader .v-skeleton-loader__list-item) {
    background-color: transparent;
  }
}
</style>
